'use client'

import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'

const steps = [
  {
    number: '01',
    title: 'Discovery & Strategy',
    description: 'Deep dive into your goals, audience, and competitive landscape to define a clear digital strategy.',
  },
  {
    number: '02',
    title: 'Design & Prototyping',
    description: 'Figma wireframes to high-fidelity prototypes — reviewed, refined, and approved before a single line of code.',
  },
  {
    number: '03',
    title: 'Development',
    description: 'Engineering with precision — clean code, modern stack, performance-first architecture.',
  },
  {
    number: '04',
    title: 'Quality Assurance',
    description: 'Rigorous cross-device testing, performance audits, and User Acceptance Testing before go-live.',
  },
  {
    number: '05',
    title: 'Launch & Support',
    description: "Seamless deployment and ongoing support — we don't disappear after delivery.",
  },
]

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How Apex Visualtech Builds a Website or Digital Product',
  description: 'The proven 5-step process Apex Visualtech uses to deliver high-performance websites, applications, and digital experiences for clients across India.',
  totalTime: 'P6W',
  estimatedCost: {
    '@type': 'MonetaryAmount',
    currency: 'INR',
    value: '25000',
  },
  step: steps.map((s, i) => ({
    '@type': 'HowToStep',
    position: i + 1,
    name: s.title,
    text: s.description,
  })),
}

function Step({ step, index }: { step: typeof steps[0]; index: number }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-50px' })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col group"
    >
      {/* Connector line (desktop) */}
      <div className="hidden lg:flex items-center mb-8">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#C8A96E]/30" />
        <div className="w-3 h-3 border border-[#C8A96E] rotate-45 flex-shrink-0 group-hover:bg-[#C8A96E] transition-colors duration-300" />
      </div>

      <span
        className="text-[#C8A96E]/30 text-4xl leading-none mb-4 group-hover:text-[#C8A96E]/60 transition-colors duration-300"
        style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
      >
        {step.number}
      </span>
      <h3
        className="text-xl text-[#F8FAFC] mb-3 group-hover:text-[#C8A96E] transition-colors duration-300"
        style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.05em' }}
      >
        {step.title}
      </h3>
      <p className="text-[#94A3B8] text-sm leading-relaxed">
        {step.description}
      </p>
    </motion.div>
  )
}

export default function Process() {
  return (
    <section className="py-32 bg-[#1E293B]">
      {/* HowTo Schema for AEO — tells Google and AI engines how we work step-by-step */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-20"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-px bg-[#C8A96E]" />
            <span
              className="text-[#C8A96E] text-xs tracking-[0.3em]"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
            >
              OUR PROCESS
            </span>
          </div>
          <h2
            className="text-[clamp(3rem,8vw,7rem)] leading-none text-[#F8FAFC]"
            style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.02em' }}
          >
            HOW WE WORK
          </h2>
        </motion.div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-4">
          {steps.map((step, index) => (
            <Step key={step.number} step={step} index={index} />
          ))}
        </div>
      </div>
    </section>
  )
}
